// Shared create/edit-class dialog — one implementation reused by the Classes list
// and Class Detail (same rule as studentForm.ts: don't duplicate the edit UI).
import { repos } from './store.js';
import { formDialog, toast } from './dom.js';
import type { ClassRecord } from '../db/schema.js';

const GRADES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

/** The DepEd school year starts mid-year (~June/August), so from June onward
 *  the current SY is this calendar year, otherwise the previous one. */
export function currentSchoolYear(): string {
  const now = new Date();
  const start = now.getMonth() >= 5 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}-${start + 1}`;
}

/** A small window of selectable school years around the current one. `include`
 *  keeps an existing class's SY selectable even if it falls outside the window,
 *  so editing another field can never silently move the class to another year. */
function schoolYearOptions(include?: string): Array<{ value: string; label: string }> {
  const now = new Date();
  const start = now.getMonth() >= 5 ? now.getFullYear() : now.getFullYear() - 1;
  const years: string[] = [];
  for (let y = start - 2; y <= start + 3; y++) years.push(`${y}-${y + 1}`);
  if (include && !years.includes(include)) years.unshift(include);
  return years.map((y) => ({ value: y, label: y }));
}

export async function classFormDialog(opts: {
  /** the class being edited; omit to create a new one. */
  existing?: ClassRecord;
  onDone?: (cls: ClassRecord) => void;
}): Promise<void> {
  const { existing } = opts;
  const editing = !!existing;

  const v = await formDialog(
    editing ? 'Edit class' : 'New class',
    [
      { name: 'name', label: 'Class name', placeholder: 'e.g. Grade 9 – Rizal', value: existing?.name, required: true, maxlength: 60 },
      // No default grade on create — the teacher picks it (required), so nothing
      // is recorded by accident.
      {
        name: 'gradeLevel',
        label: 'Grade level',
        type: 'select',
        value: existing?.gradeLevel ?? '',
        required: true,
        options: [{ value: '', label: 'Select…' }, ...GRADES.map((g) => ({ value: g, label: 'Grade ' + g }))],
      },
      {
        name: 'schoolYear',
        label: 'School year',
        type: 'select',
        value: existing?.schoolYear ?? currentSchoolYear(),
        options: schoolYearOptions(existing?.schoolYear),
      },
    ],
    editing ? 'Save' : 'Create',
  );
  if (!v) return;

  try {
    if (editing) {
      const patch = { name: v.name, gradeLevel: v.gradeLevel, schoolYear: v.schoolYear };
      await repos.updateClass(existing!.id, patch);
      toast('Class updated');
      opts.onDone?.({ ...existing!, ...patch });
    } else {
      const cls = await repos.createClass({ name: v.name, gradeLevel: v.gradeLevel, schoolYear: v.schoolYear });
      toast('Class created');
      opts.onDone?.(cls);
    }
  } catch (e: any) {
    toast(e.message, 'err');
  }
}
